import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  generateSitemapXml, 
  getSitemapStats, 
  downloadSitemap, 
  copySitemapToClipboard,
  getAllUrls
} from '@/utils/sitemapGenerator';
import { Download, Copy, Check, FileText, MapPin, Newspaper, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const SitemapPage = () => {
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<ReturnType<typeof getSitemapStats> | null>(null);
  const [urls, setUrls] = useState<ReturnType<typeof getAllUrls>>([]);

  useEffect(() => {
    setStats(getSitemapStats());
    setUrls(getAllUrls());
  }, []);

  const handleCopy = async () => {
    const success = await copySitemapToClipboard();
    if (success) {
      setCopied(true);
      toast.success('Sitemap XML copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy sitemap');
    }
  };

  const handleDownload = () => {
    downloadSitemap();
    toast.success('Sitemap downloaded!');
  };

  return (
    <>
      <Helmet>
        <title>Sitemap Generator | PK Landscape Associates</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <Navbar />
      
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-foreground mb-2">Sitemap Generator</h1>
            <p className="text-muted-foreground mb-8">
              Automatically generates sitemap.xml from your blog posts, services, and location pages.
            </p>

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <div className="text-3xl font-bold text-primary">{stats.totalUrls}</div>
                    <div className="text-sm text-muted-foreground">Total URLs</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <FileText className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                    <div className="text-2xl font-bold">{stats.staticPages}</div>
                    <div className="text-xs text-muted-foreground">Static Pages</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Wrench className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                    <div className="text-2xl font-bold">{stats.services}</div>
                    <div className="text-xs text-muted-foreground">Services</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <MapPin className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                    <div className="text-2xl font-bold">{stats.locations}</div>
                    <div className="text-xs text-muted-foreground">Locations</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Newspaper className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                    <div className="text-2xl font-bold">{stats.blogPosts}</div>
                    <div className="text-xs text-muted-foreground">Blog Posts</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 mb-8">
              <Button onClick={handleDownload} className="gap-2">
                <Download className="h-4 w-4" />
                Download sitemap.xml
              </Button>
              <Button variant="outline" onClick={handleCopy} className="gap-2">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>

            {/* URL List */}
            <Card>
              <CardHeader>
                <CardTitle>All URLs ({urls.length})</CardTitle>
                <CardDescription>
                  These URLs are automatically generated from your data files. Add new blog posts, services, or locations and they'll appear here.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {urls.map((url, index) => (
                    <div key={index} className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded text-sm">
                      <span className="text-foreground font-mono truncate flex-1">
                        {url.loc}
                      </span>
                      <div className="flex gap-2 ml-4">
                        <Badge variant="outline" className="text-xs">
                          {url.changefreq}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {url.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>How to Update Your Sitemap</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <ol className="list-decimal list-inside space-y-2">
                  <li>Click "Download sitemap.xml" to get the latest version</li>
                  <li>Replace the file at <code className="bg-muted px-1 rounded">public/sitemap.xml</code></li>
                  <li>Deploy your changes</li>
                  <li>Resubmit to Google Search Console if needed</li>
                </ol>
                <p className="mt-4">
                  <strong>Tip:</strong> Whenever you add new blog posts, services, or locations to your data files, 
                  come back here to regenerate and update your sitemap.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default SitemapPage;
